import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import UseVendorHook from "hooks/UseVendorHook";

const AddVendorModal = ({ isOpen, onClose, onAddVendor, initialData }) => {
  const mapVendorData = (data = {}) => ({
    _id: data?._id || "",
    name: data?.name || "",
    email: data?.email || "",
    phone: data?.mobile || "",
    address: data?.address || "",
    status: data?.active ? "active" : "inactive",

    // vendor fields
    businessName: data?.vendorDetails?.businessName || "",
    access_token: data?.vendorDetails?.access_token || "",
    instanceId: data?.vendorDetails?.instanceId || "",
    ownerNumber: data?.vendorDetails?.ownerNumber || "",
    thankYouMessage: data?.vendorDetails?.thankYouMessage || "",
    currency: data?.vendorDetails?.currency || "INR",
    logoUrl: data?.vendorDetails?.logoUrl || "",
    country: data?.vendorDetails?.country || "91", // ✅ default India code

    // subscription
    subscription: {
      plan: data?.vendorDetails?.subscription?.plan || "free",
      status: data?.vendorDetails?.subscription?.status || "trial",
    },
  });

  const [formData, setFormData] = useState(() => mapVendorData(initialData));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createVendor, editVendor } = UseVendorHook();

  useEffect(() => {
    if (initialData) {
      setFormData(mapVendorData(initialData));
    }
  }, [initialData]);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "pending", label: "Pending" },
  ];

  const planOptions = [
    { value: "free", label: "Free" },
    { value: "basic", label: "Basic" },
    { value: "pro", label: "Pro" },
    { value: "enterprise", label: "Enterprise" },
  ];

  const subscriptionStatusOptions = [
    { value: "trial", label: "Trial" },
    { value: "active", label: "Active" },
    { value: "expired", label: "Expired" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedChange = (nestedField, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [nestedField]: { ...prev[nestedField], [field]: value },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Vendor name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.businessName.trim())
      newErrors.businessName = "Business name is required";
    if (!formData.ownerNumber.trim())
      newErrors.ownerNumber = "Owner number is required";
    if (!formData.country.trim())
      newErrors.country = "Country code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const val = {
        ...(initialData?._id && { _id: formData._id }),
        name: formData.name,
        email: formData.email,
        mobile: formData.phone,
        address: formData?.address,
        active: formData.status === "active",
        vendorDetails: {
          businessName: formData?.businessName,
          access_token: formData?.access_token,
          instanceId: formData?.instanceId,
          ownerNumber: formData?.ownerNumber,
          thankYouMessage: formData?.thankYouMessage,
          currency: formData?.currency,
          logoUrl: formData?.logoUrl,
          country: formData?.country, // ✅ Added
          subscription: formData.subscription,
        },
      };

      if (initialData) {
        await editVendor(val);
      } else {
        await createVendor(val);
      }

      handleClose();
    } catch (error) {
      console.error("Error adding vendor:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(mapVendorData());
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1200 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-modal w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {initialData ? "Edit Vendor" : "Add New Vendor"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {initialData
                  ? "Update vendor details"
                  : "Create a new vendor account"}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

         <form onSubmit={handleSubmit} className="space-y-4">
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <Input
      label="Vendor Name"
      type="text"
      placeholder="Enter vendor name"
      value={formData.name}
      onChange={(e) => handleInputChange("name", e.target.value)}
      error={errors.name}
      required
    />

    <Input
      label="Email Address"
      type="email"
      placeholder="Enter email address"
      value={formData.email}
      onChange={(e) => handleInputChange("email", e.target.value)}
      error={errors.email}
      required
    />

    <Input
      label="Phone Number"
      type="tel"
      placeholder="Enter phone number"
      value={formData.phone}
      onChange={(e) => handleInputChange("phone", e.target.value)}
      error={errors.phone}
      required
    />

    <Input
      label="Country Code"
      type="text"
      placeholder="e.g. 91 for India"
      value={formData.country}
      onChange={(e) => handleInputChange("country", e.target.value)}
      error={errors.country}
      required
    />

    <Input
      label="Address"
      type="text"
      placeholder="Enter business address"
      value={formData.address}
      onChange={(e) => handleInputChange("address", e.target.value)}
    />

    <Input
      label="Business Name"
      type="text"
      placeholder="Enter business name"
      value={formData.businessName}
      onChange={(e) => handleInputChange("businessName", e.target.value)}
      error={errors.businessName}
      required
    />

    <Input
      label="Access Token"
      type="text"
      placeholder="Enter access token"
      value={formData.access_token}
      onChange={(e) => handleInputChange("access_token", e.target.value)}
    />

    <Input
      label="Instance ID"
      type="text"
      placeholder="Enter instance ID"
      value={formData.instanceId}
      onChange={(e) => handleInputChange("instanceId", e.target.value)}
    />

    <Input
      label="Owner Number"
      type="text"
      placeholder="Enter owner number"
      value={formData.ownerNumber}
      onChange={(e) => handleInputChange("ownerNumber", e.target.value)}
      error={errors.ownerNumber}
      required
    />

    <Input
      label="Thank You Message"
      type="text"
      placeholder="Enter thank you message"
      value={formData.thankYouMessage}
      onChange={(e) => handleInputChange("thankYouMessage", e.target.value)}
    />

    <Input
      label="Logo URL"
      type="text"
      placeholder="Enter logo URL"
      value={formData.logoUrl}
      onChange={(e) => handleInputChange("logoUrl", e.target.value)}
    />

    <Input
      label="Currency"
      type="text"
      placeholder="Enter currency"
      value={formData.currency}
      onChange={(e) => handleInputChange("currency", e.target.value)}
    />

    <Select
      label="Subscription Plan"
      options={planOptions}
      value={formData.subscription.plan}
      onChange={(value) => handleNestedChange("subscription", "plan", value)}
      required
    />

    <Select
      label="Subscription Status"
      options={subscriptionStatusOptions}
      value={formData.subscription.status}
      onChange={(value) => handleNestedChange("subscription", "status", value)}
      required
    />

    <Select
      label="Status"
      options={statusOptions}
      value={formData.status}
      onChange={(value) => handleInputChange("status", value)}
      required
    />
  </div>

  <div className="flex space-x-3 pt-4">
    <Button
      type="button"
      variant="outline"
      fullWidth
      onClick={handleClose}
      disabled={isSubmitting}
    >
      Cancel
    </Button>
    <Button
      type="submit"
      fullWidth
      loading={isSubmitting}
      iconName={initialData ? "Edit" : "Plus"}
      iconPosition="left"
      iconSize={16}
    >
      {initialData ? "Update Vendor" : "Add Vendor"}
    </Button>
  </div>
</form>

        </div>
      </div>
    </div>
  );
};

export default AddVendorModal;
